import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Minus, Plus, ShoppingCart, AlertCircle, Check } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";
import { tierUnitPrice, fmtUSD } from "../lib/pricing";
import NextTierCallout from "../components/NextTierCallout";
import ProductBadges from "../components/ProductBadges";
import ResearchStacks from "../components/ResearchStacks";
import SynergyRecommendations from "../components/SynergyRecommendations";
import ProductImage from "../components/ProductImage";
import PairsWellWith from "../components/PairsWellWith";
import RelatedProducts from "../components/RelatedProducts";
import SEO, { buildProductJsonLd, buildBreadcrumbJsonLd } from "../components/SEO";
import LabVerifiedBadge from "../components/LabVerifiedBadge";
import ProductLongContent from "../components/ProductLongContent";
import { getProductForm, getProductFAQs, getProductMeta } from "../lib/productMeta";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      setVariant(data.variants[0]);
    });
  }, [slug]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#8A94A6]">Loading…</div>;

  // Legacy typo slug hit? Redirect to the canonical URL for SEO cleanliness.
  // The backend already returns the correct product for legacy aliases, but
  // we want the browser's URL to reflect the canonical slug so Google eventually
  // drops the old URL from its index.
  if (product.slug && product.slug !== slug) {
    return <Navigate to={`/product/${product.slug}`} replace />;
  }

  const tiers = (variant?.tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  const effectiveUnit = variant ? tierUnitPrice(variant, qty) : 0;
  const listUnit = Number(variant?.price || 0);
  const lineTotal = effectiveUnit * qty;
  const saved = Math.max(0, listUnit * qty - lineTotal);

  // Highlight which tier is active
  const activeTier = tiers.reduce(
    (acc, t) => (qty >= t.min_qty ? t : acc),
    tiers[0] || null
  );

  // Form-aware descriptive noun ("research peptide" / "sterile solution" /
  // "alcohol prep pads" / "small-molecule research compound" / etc.) — drives
  // accurate SEO title, meta description, and FAQ copy for non-peptide SKUs.
  const form = getProductForm(product);
  const productNoun = ({
    solution: "sterile laboratory solution",
    pad: "sterile alcohol prep pads",
    "small-molecule": "small-molecule research compound",
    coenzyme: "research-grade coenzyme",
    protein: "recombinant research protein",
    blend: "research blend",
    peptide: "research peptide",
  })[form] || "research compound";

  const faqs = getProductFAQs(product);

  // Pull consumer-friendly copy from productMeta.js so runtime SEO tags
  // match the pre-baked static HTML AND the visible PDP overview. Falls
  // back to the older template only when a slug has no meta entry (should
  // not happen — every prod slug has one).
  const meta = getProductMeta(product.slug) || {};
  const humanName = meta.fullName || product.name;
  const overviewSeo = meta.overview
    ? (() => {
        const raw = meta.overview.replace(/\s+/g, " ").trim();
        const firstDot = raw.indexOf(". ");
        return firstDot > 60 && firstDot < 170 ? raw.slice(0, firstDot + 1) : raw.slice(0, 160);
      })()
    : "";
  const isCosmetic = form === "cosmetic";
  const price = Number(variant?.price || tiers[0]?.unit_price || 0).toFixed(2);
  const seoDescription = overviewSeo
    ? isCosmetic
      ? `${overviewSeo} 2% GHK-Cu + 3% SNAP-8. Made in USA. From $${price}.`
      : `${overviewSeo} Third-party HPLC verified · fast US shipping · from $${price}. For research purposes only.`
    : isCosmetic
      ? `Buy ${product.name} multi-peptide facial serum${variant?.size ? ` (${variant.size})` : ""} from ProSource Labs — professional-strength 2% GHK-Cu and 3% SNAP-8 with hyaluronic acid, niacinamide, and panthenol. Made in USA. ${product.summary || ""}`
      : `Buy ${product.name} ${productNoun}${variant?.size ? ` (${variant.size} vial)` : ""} from ProSource Labs — ${form === "solution" || form === "pad" ? "shipped from Southern California with lot traceability" : "third-party HPLC verified for identity and purity"}. Tiered volume pricing available. ${product.summary || ""} For research purposes only — not for human consumption.`;
  const schemaDescription = (meta.overview || product.summary || `Research-grade ${product.name}.`).slice(0, 500);
  const kfLabels = (meta.keyFacts || [])
    .map((f) => {
      const i = f.indexOf(":");
      return i > 0 && i < 40 ? f.slice(0, i).toLowerCase() : "";
    })
    .filter(Boolean);
  // Keyword strategy:
  //  - If the product's productMeta entry declares an explicit `keywords`
  //    string (e.g. Blue Renewal's consumer-facing skincare keyword set),
  //    use that verbatim — it's tuned for the product's category and audience.
  //  - Otherwise, auto-generate a peptide-flavored keyword set from the
  //    product's name/category/keyFact labels, INCLUDING the "research"
  //    keyword for peptide/nasal-spray/supply products. For cosmetic
  //    products, we drop "research" from the auto-set so we never emit
  //    off-brand keywords.
  const seoKeywords = meta?.keywords
    ? meta.keywords
    : [
        product.name.toLowerCase(),
        `buy ${product.name.toLowerCase()}`,
        isCosmetic ? "" : `${product.name.toLowerCase()} research`,
        (product.category || "").toLowerCase(),
        productNoun,
        ...kfLabels,
      ].filter(Boolean).join(", ");

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <SEO
        title={`${humanName}${variant?.size ? ` — ${variant.size}` : ""} · ${product.category || "Research Peptide"}`}
        titleTemplate="product"
        description={seoDescription}
        descriptionTagline={isCosmetic ? "Made in USA at ProSource Labs, Corona, California. Precision. Purity. Performance." : undefined}
        path={`/product/${slug}`}
        image={product.image || product.thumbnail}
        type="product"
        keywords={seoKeywords}
        jsonLd={[
          buildProductJsonLd({
            name: humanName,
            description: schemaDescription,
            image: product.image || product.thumbnail,
            sku: variant?.sku || product.slug,
            slug: product.slug,
            priceFrom: price,
            category: product.category,
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            ...(product.category ? [{ name: product.category, path: `/category/${(product.category || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}` }] : []),
            { name: product.name, path: `/product/${slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <Link to="/shop" className="overline hover:text-white transition-colors">← BACK TO CATALOG</Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-10">
        <div className="vial-bg aspect-square border border-white/10 flex items-center justify-center relative overflow-hidden">
          <div className="absolute top-6 left-6 overline" style={{ fontSize: 10 }}>{product.category}</div>
          {variant?.sku && (
            <div className="absolute top-6 right-6 font-mono-tech text-[10px] text-[#5C657A] tracking-wider">
              SKU · {variant.sku}
            </div>
          )}
          {/*
            Sizing: peptide vials are portrait/tall, so 68% × 88% frames them nicely.
            Cosmetic products (Blue Renewal) are landscape composition (box + bottle
            side-by-side) — for those we top-anchor the picture element and extend
            it down to the container bottom, then bottom-align the image inside the
            picture via object-position so the product photo visually "sits on"
            the container's floor line (matching owner's request: keep top margin,
            bring bottom flush to section line). object-position toggle lives in
            ProductImage.jsx (isCosmetic branch).
          */}
          <ProductImage
            product={product}
            size={variant?.size}
            className={isCosmetic ? "w-full h-[86%] self-start mt-[14%]" : "w-[68%] h-[88%]"}
          />
          {/* isCosmetic sizing above: 14% top offset leaves room for SKINCARE
              category chip + SKU text baked into the container's top-6
              positioning. h-[86%] fills the remaining 86% down to the bottom
              container line. Combined with objectFit:cover in ProductImage,
              the product photo fills the entire remaining square. */}
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="overline">{product.category}</div>
            <LabVerifiedBadge size="lg" slug={product.slug} />
          </div>
          <h1 data-testid="product-name" className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3">
            {product.name}
            <span className="block text-lg sm:text-xl text-[#00C3FF] mt-2 font-mono-tech tracking-wide">
              {form === "solution" && "Sterile Laboratory Solution · Lot-Traced"}
              {form === "pad" && "Sterile 70% IPA Wipes · Lot-Traced"}
              {form === "small-molecule" && "Small-Molecule Research Compound · Lab-Verified"}
              {form === "coenzyme" && "Research-Grade Coenzyme · Lab-Verified Purity"}
              {form === "protein" && "Recombinant Research Protein · Lab-Verified Purity"}
              {form === "blend" && "Multi-Compound Research Blend · Lab-Verified"}
              {form === "peptide" && "Research-Grade Peptide · Lab-Verified Purity"}
            </span>
          </h1>
          <ProductBadges slug={product.slug} />

          <p className="text-sm text-[#8A94A6] mt-6 leading-relaxed max-w-md">{meta.overview || product.summary}</p>

          {/* Size selector */}
          <div className="mt-10">
            <div className="overline mb-4">Select Size</div>
            <div className={`grid gap-px bg-white/10 border border-white/10 ${product.variants.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
              {product.variants.map((v) => (
                <button
                  key={v.size}
                  data-testid={`variant-${v.size}`}
                  onClick={() => { setVariant(v); setQty(1); }}
                  className={`bg-[#0C0C12] p-4 transition-colors text-left ${
                    variant?.size === v.size ? "ring-1 ring-[#00C3FF] z-10" : "hover:bg-[#14141C]"
                  }`}
                >
                  <div className="font-mono-tech text-base uppercase">{v.size}</div>
                  <div className="font-mono-tech text-xs text-[#00C3FF] mt-1">From {fmtUSD(v.price)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tier price breaks */}
          {tiers.length > 0 && (
            <div className="mt-10">
              <div className="overline mb-4">Volume Pricing</div>
              <div className="grid grid-cols-4 gap-px bg-white/10 border border-white/10">
                {tiers.map((t) => {
                  const isActive = activeTier && t.min_qty === activeTier.min_qty;
                  const totalAtQty = (t.unit_price * t.min_qty).toFixed(2);
                  return (
                    <button
                      key={t.min_qty}
                      data-testid={`tier-${t.min_qty}`}
                      onClick={() => setQty(t.min_qty)}
                      className={`bg-[#0C0C12] p-3 text-left transition-colors relative ${
                        isActive ? "ring-1 ring-[#00C3FF] z-10" : "hover:bg-[#14141C]"
                      }`}
                    >
                      {isActive && (
                        <Check size={12} className="absolute top-2 right-2 text-[#00C3FF]" />
                      )}
                      <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#5C657A] uppercase">QTY {t.min_qty}{t.min_qty === tiers[tiers.length - 1].min_qty ? "+" : ""}</div>
                      <div className="font-mono-tech text-sm text-white mt-2">${t.unit_price.toFixed(2)}<span className="text-[#5C657A] text-[10px]"> /unit</span></div>
                      <div className="font-mono-tech text-[10px] text-[#5C657A] mt-1">{fmtUSD(totalAtQty)} total</div>
                    </button>
                  );
                })}
              </div>
              {saved > 0 && (
                <div data-testid="tier-savings-badge" className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono-tech text-[#00C3FF] uppercase tracking-[0.2em]">
                  <Check size={12} /> You save {fmtUSD(saved)}
                </div>
              )}
              <NextTierCallout tiers={tiers} qty={qty} listUnit={listUnit || tiers[0].unit_price} onPick={setQty} />
            </div>
          )}

          <div className="mt-10">
            <div className="overline mb-4">Quantity</div>
            <div className="inline-flex items-center border border-white/10">
              <button
                data-testid="qty-decrement"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-12 h-12 hover:bg-white/5 flex items-center justify-center"
              >
                <Minus size={14} />
              </button>
              <span data-testid="qty-value" className="w-14 text-center font-mono-tech">{qty}</span>
              <button
                data-testid="qty-increment"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                className="w-12 h-12 hover:bg-white/5 flex items-center justify-center"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-baseline gap-4">
            <div data-testid="line-total" className="font-mono-tech text-3xl">{fmtUSD(lineTotal)}</div>
            <div className="text-xs font-mono-tech text-[#5C657A]">
              {fmtUSD(effectiveUnit)} × {qty}
              {saved > 0 && <span className="text-[#00C3FF] ml-3">· saved {fmtUSD(saved)}</span>}
            </div>
          </div>

          <button
            data-testid="add-to-cart-button"
            disabled={!variant}
            onClick={() => variant && addItem(product, variant, qty)}
            className="btn-primary mt-8 justify-center"
          >
            <ShoppingCart size={14} /> Add to Cart
          </button>

          <div className="mt-10 surface p-5 flex gap-3">
            <AlertCircle size={16} className="text-[#00C3FF] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#8A94A6] leading-relaxed">
              {isCosmetic ? (
                <>
                  <span className="text-white font-mono-tech tracking-wider uppercase text-[10px] block mb-1">External Use Only</span>
                  {product.name} is a cosmetic skincare product for external use only. Discontinue use if irritation occurs. Keep out of reach of children. Manufactured in the USA by ProSource Labs, Corona, California.
                </>
              ) : (
                <>
                  <span className="text-white font-mono-tech tracking-wider uppercase text-[10px] block mb-1">For Research Use Only</span>
                  This product is sold for research purposes only and is not intended for human or animal
                  consumption, diagnostic, or therapeutic use.
                </>
              )}
            </div>
          </div>

          <Link
            to="/wholesale"
            className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#5C657A] hover:text-[#00C3FF] mt-6 inline-block"
          >
            Need more than 10? Contact us for wholesale pricing →
          </Link>
        </div>
      </div>

      <PairsWellWith
        currentSlug={product.slug}
        currentCategory={product.category}
      />

      <SynergyRecommendations product={product} />

      <ResearchStacks currentSlug={product.slug} />

      {/* Long-form SEO content — auto-generated per product (~1,000-1,200 words) */}
      <ProductLongContent product={product} />

      {/* Related products — 5–10 internal links per PDP for SEO depth + discovery */}
      <RelatedProducts
        currentSlug={product.slug}
        currentCategory={product.category}
        max={8}
      />

      {/* Visible FAQ section — matches the FAQPage JSON-LD schema above so Google
          shows FAQ rich snippets in SERP. Compliance-safe: chemistry, purity,
          shipping, and wholesale only. NO dosing/reconstitution/administration. */}
      <section className="mt-20 border-t border-white/10 pt-16" data-testid="product-faq-section">
        <div className="overline">FREQUENTLY ASKED QUESTIONS</div>
        <h2 className="font-heading text-3xl sm:text-4xl mt-3 mb-10 font-light">
          About {product.name}
        </h2>
        <div className="space-y-6 max-w-3xl">
          {faqs.map((item, i) => (
            <div key={i} className="border-b border-white/5 pb-6">
              <h3 className="font-heading text-lg text-white mb-2">{item.q}</h3>
              <p className="text-sm text-[#8A94A6] leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
